import {create} from 'zustand';
import {BaseMutation} from '@app/editor/mutations/base-mutation';
import {editorState} from '@app/editor/state/editor-store';
import {inspectorState} from '@app/editor/state/inspector-store';
import {
  HoveredElementOverlay,
  SelectedElementOverlay,
} from '@app/editor/elements/element-overlays/element-overlays';
import {layoutEditorState} from '@app/editor/editor-sidebar/layout-editor/layout-editor-store';
import {getProjectEditorUrl} from '@app/projects/project-link';
import {
  captureInitialPages,
  capturePageHtml,
  loadHistory,
  PersistedHistory,
  PersistedHistoryEntry,
  saveHistory,
} from '@app/editor/state/history-persistence';
import {SnapshotMutation} from '@app/editor/mutations/dom/snapshot-mutation';
import {createDocFromHtml} from '@app/editor/utils/create-doc-from-html';

export interface ExecuteMutationOptions {
  partOfSession?: boolean;
  lastInSession?: boolean;
  skipHistory?: boolean;
  forceSyncEditor?: boolean;
  forceAddToHistory?: boolean;
}

interface State {
  isDirty: boolean;
  mutationStack: BaseMutation[];
  canUndo: boolean;
  canRedo: boolean;
  pointer: number;
  persistedInitialPages: Record<string, string>;
}

const maxHistory = 500;

const initialState: State = {
  isDirty: false,
  mutationStack: [],
  canUndo: false,
  canRedo: false,
  pointer: -1,
  persistedInitialPages: {},
};

interface Actions {
  executeMutation: (
    mutation: BaseMutation,
    options: ExecuteMutationOptions,
  ) => boolean;
  undo: () => void;
  redo: () => void;
  goTo: (index: number) => void;
  setPointer: (pointer: number) => void;
  reset: () => void;
  setIsDirty: (isDirty: boolean) => void;
  initPersistence: () => void;
  restoreHistory: () => Promise<void>;
}

interface SessionCacheItem {
  executed: boolean;
  mutation: BaseMutation;
}
let sessionCache: SessionCacheItem[] = [];

// Map mutation uniqueId -> snapshot of page HTML after that mutation was applied
const snapshotCache = new Map<
  string,
  {pageName: string; afterHtml: string}
>();

let historyRestored = false;

export const useMutationStore = create<State & Actions>()((set, get) => {
  return {
    ...initialState,
    setIsDirty: isDirty => set({isDirty}),
    reset: () => {
      snapshotCache.clear();
      historyRestored = false;
      set(initialState);
    },
    executeMutation: (mutation, options) => {
      let executed = mutation.init().execute();
      const newSessionCacheItem = {executed, mutation};

      // if it's a different mutation class or last item
      // in session, clear session and add to history
      const addToHistory =
        !options?.skipHistory &&
        (options?.lastInSession ||
          (sessionCache.length &&
            (sessionCache[0].mutation.constructor !== mutation.constructor ||
              sessionCache[0].mutation.getNodeId() !== mutation.getNodeId())));

      if (addToHistory) {
        const merged = mergeMutations([...sessionCache, newSessionCacheItem]);
        executed = merged.executed;
        mutation = merged.mutation;
      }

      if (options?.lastInSession) {
        sessionCache = [];
      }

      if (options?.partOfSession) {
        sessionCache.push(newSessionCacheItem);
      }

      if (executed) {
        if (addToHistory) {
          // remove all stack items after new pointer (if pushing after undoing)
          let newStack = [...get().mutationStack];
          if (get().canRedo) {
            newStack = newStack.slice(0, get().pointer + 1);
          }
          newStack.push(mutation);

          if (newStack.length > maxHistory) {
            newStack = newStack.slice(newStack.length - maxHistory);
          }

          set({
            mutationStack: newStack,
          });

          get().setPointer(Math.min(maxHistory - 1, get().pointer + 1));
          captureAndPersist(mutation);
        }
        syncEditor(!options?.forceSyncEditor && options?.partOfSession);
      }

      return executed;
    },
    undo: () => {
      const mutation = get().mutationStack[get().pointer];
      if (mutation) {
        mutation.undo();
        get().setPointer(get().pointer - 1);
        syncEditor();
        persistToStorage();
      }
    },
    redo() {
      const mutation = get().mutationStack[get().pointer + 1];
      if (mutation) {
        mutation.redo();
        get().setPointer(get().pointer + 1);
        syncEditor();
        persistToStorage();
      }
    },
    goTo(index: number) {
      if (index === get().pointer) return;

      if (index < get().pointer) {
        for (let i = get().pointer; i > index; i--) {
          get().mutationStack[i].undo();
        }
      } else {
        for (let i = get().pointer + 1; i <= index; i++) {
          get().mutationStack[i].redo();
        }
      }

      get().setPointer(index);
      syncEditor();
      persistToStorage();
    },
    setPointer(newPointer: number) {
      set({
        pointer: newPointer,
        canUndo: newPointer !== -1,
        canRedo: newPointer < get().mutationStack.length - 1,
      });
    },
    initPersistence() {
      const initialPages = captureInitialPages();
      set({persistedInitialPages: initialPages});
    },
    async restoreHistory() {
      if (historyRestored) return;
      historyRestored = true;

      const projectId = editorState().project?.model.uuid;
      if (!projectId) return;

      const persisted = await loadHistory(projectId);
      if (!persisted || persisted.entries.length === 0) {
        // No history to restore, just capture initial pages
        get().initPersistence();
        return;
      }

      // Build beforeHtml for each entry by looking at previous entries
      const snapshots: SnapshotMutation[] = [];
      for (let i = 0; i < persisted.entries.length; i++) {
        const entry = persisted.entries[i];
        // Find beforeHtml: look backward for most recent entry with same pageName
        let beforeHtml = persisted.initialPages[entry.pageName] ?? '';
        for (let j = i - 1; j >= 0; j--) {
          if (persisted.entries[j].pageName === entry.pageName) {
            beforeHtml = persisted.entries[j].afterHtml;
            break;
          }
        }

        const snap = new SnapshotMutation({
          pageName: entry.pageName,
          displayName: entry.displayName,
          beforeHtml,
          afterHtml: entry.afterHtml,
          uniqueId: entry.uniqueId,
          createdAt: entry.createdAt,
        });
        snapshots.push(snap);

        // Pre-populate snapshot cache so persistence works for restored entries
        snapshotCache.set(entry.uniqueId, {
          pageName: entry.pageName,
          afterHtml: entry.afterHtml,
        });
      }

      // Apply the HTML state at the persisted pointer
      const pointer = persisted.pointer;
      // For each page, find the most recent afterHtml at or before the pointer
      const pageStates: Record<string, string> = {...persisted.initialPages};
      for (let i = 0; i <= pointer && i < persisted.entries.length; i++) {
        const entry = persisted.entries[i];
        pageStates[entry.pageName] = entry.afterHtml;
      }

      // Apply to page docs
      const project = editorState().project;
      if (project) {
        for (const page of project.pages) {
          const html = pageStates[page.name];
          if (html) {
            if (!page.doc) {
              page.doc = createDocFromHtml(page.html);
            }
            page.doc.documentElement.innerHTML = html;
          }
        }
        // Rebuild iframe with updated active page
        const activePage = project.pages.find(
          p => p.id === editorState().activePageId,
        );
        if (activePage?.doc) {
          rebuildIframe(activePage);
        }
      }

      set({
        mutationStack: snapshots,
        persistedInitialPages: persisted.initialPages,
      });
      get().setPointer(pointer);
    },
  };
});

export function mutationState() {
  return useMutationStore.getState();
}

/**
 * Capture a snapshot of the affected page right after a mutation is applied,
 * and persist the full history to sessionStorage.
 */
function captureAndPersist(mutation: BaseMutation) {
  const pageId = (mutation as any).pageId as string;
  if (mutation instanceof SnapshotMutation) {
    // Already has snapshot data from restore
    const page = editorState().project?.pages.find(p => p.id === pageId);
    snapshotCache.set(mutation.uniqueId, {
      pageName: page?.name ?? '',
      afterHtml: (mutation as any).changes.new,
    });
  } else {
    const snapshot = capturePageHtml(pageId);
    if (snapshot) {
      snapshotCache.set(mutation.uniqueId, {
        pageName: snapshot.pageName,
        afterHtml: snapshot.html,
      });
    }
  }
  persistToStorage();
}

function persistToStorage() {
  const projectId = editorState().project?.model.uuid;
  if (!projectId) return;

  const state = useMutationStore.getState();
  const entries: PersistedHistoryEntry[] = [];

  for (const mutation of state.mutationStack) {
    const cached = snapshotCache.get(mutation.uniqueId);
    if (cached) {
      entries.push({
        uniqueId: mutation.uniqueId,
        createdAt: mutation.createdAt,
        displayName: mutation.displayName,
        pageName: cached.pageName,
        afterHtml: cached.afterHtml,
      });
    }
  }

  const history: PersistedHistory = {
    initialPages: state.persistedInitialPages,
    entries,
    pointer: state.pointer,
  };

  saveHistory(projectId, history);
}

function rebuildIframe(page: {doc?: Document; name: string}) {
  const iframe = editorState().getIframe();
  if (!iframe || !page.doc) return;

  const project = editorState().project;
  if (!project) return;

  // Create a base tag for relative URLs
  const base = page.doc.createElement('base');
  base.href = getProjectEditorUrl(project);
  page.doc.head.prepend(base);
  const outerHTML = page.doc.documentElement.outerHTML;
  base.remove();
  iframe.srcdoc = outerHTML;
}

function syncEditor(partial = false) {
  const ctx = editorState().selectedContext;
  if (ctx) {
    if (!partial) {
      inspectorState().setInitialConfig(ctx);
    }
    if (!editorState().isResizing) {
      SelectedElementOverlay.reposition();
    }
  }
  if (!partial) {
    layoutEditorState().loadContainers();
    mutationState().setIsDirty(true);
  }
  if (!editorState().isResizing) {
    HoveredElementOverlay.remove();
  }
}

function mergeMutations(stack: SessionCacheItem[]): SessionCacheItem {
  if (stack.length < 2) return stack[0];
  const first = stack[0];
  const last = stack[stack.length - 1];
  last.mutation.overrideInitialValue(first.mutation.getInitialValue());
  last.executed = stack.some(s => s.executed);
  return last;
}
